import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import Tooltip from 'components/v2/Tooltip';
import { sluggify } from 'utils/localization/utils';
import formatMoney from 'utils/formatters/Money';
import { useTranslation } from 'react-i18next';
import { titleize } from 'underscore.string';

const Adjustments = ({ items, onSelect, selectedItems, isItemsUnmapped }) => {
  const { t } = useTranslation();
  const sum = items.reduce((s, item) => {
    return s + item.value || 0;
  }, 0);

  const totalShipperCost = isItemsUnmapped
    ? items.reduce((total, item) => {
        if (!item.shipper_cost) return total;
        return total + item.shipper_cost;
      }, 0)
    : 'N/A';

  const totalCarrierCost = isItemsUnmapped
    ? items.reduce((total, item) => {
        if (!item.carrier_cost) return total;
        return total + item.carrier_cost;
      }, 0)
    : 'N/A';

  return (
    <div>
      <table className="default-table">
        <thead>
          <tr>
            <th>{t('DESCRIPTION', { count: 1 })}</th>
            {isItemsUnmapped && <th>{t('SHIPPER_COST')}</th>}
            {isItemsUnmapped && <th>{t('CARRIER_COST')}</th>}
            {!isItemsUnmapped && <th>{t('COST')}</th>}
            {!isItemsUnmapped && <th>{titleize(t('DATE', { count: 1 }))}</th>}
            <th>{t('SELECT')}</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => {
            return (
              <tr key={item._id}>
                <td className="">{t(sluggify(item.message))}</td>
                {!isItemsUnmapped && (
                  <td className="">
                    {formatMoney(item.value, item?.currency)}
                  </td>
                )}
                {!isItemsUnmapped && (
                  <td className="">{moment(item.timestamp).format('L LT')}</td>
                )}
                {isItemsUnmapped && (
                  <td className="">
                    {formatMoney(
                      item.shipper_cost,
                      item?.currency || DEFAULT_CURRENCY,
                    )}
                  </td>
                )}
                {isItemsUnmapped && (
                  <td className="">
                    {formatMoney(
                      item.carrier_cost,
                      item?.currency || DEFAULT_CURRENCY,
                    )}
                  </td>
                )}
                <td className="">
                  <input
                    type="checkbox"
                    checked={selectedItems.includes(item._id)}
                    onClick={(e) => onSelect(item, e.target.checked)}
                  />
                </td>
              </tr>
            );
          })}
          <tr>
            <td>{t('TOTAL')}</td>
            {!isItemsUnmapped && (
              <td>
                {formatMoney(sum, items[0]?.currency || DEFAULT_CURRENCY)}
              </td>
            )}
            {isItemsUnmapped && (
              <td>
                {formatMoney(
                  totalShipperCost,
                  items[0]?.currency || DEFAULT_CURRENCY,
                )}
              </td>
            )}
            {isItemsUnmapped && (
              <td>
                {formatMoney(
                  totalCarrierCost,
                  items[0]?.currency || DEFAULT_CURRENCY,
                )}
              </td>
            )}
            {!isItemsUnmapped && <td>&nbsp;</td>}
            <td>&nbsp;</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

export default function OrphanAdjustments({
  receivables,
  payables,
  unmappedAccessorialRequests,
  onSelect,
  selectedReceivables,
  selectedPayables,
  selectedUnmappedAccessorialRequests,
}) {
  const { t } = useTranslation();
  const receivablesCount = receivables.length;
  const payablesCount = payables.length;
  const unmappedAccessorialRequestsCount = unmappedAccessorialRequests.length;
  if (
    receivablesCount === 0 &&
    payablesCount === 0 &&
    unmappedAccessorialRequestsCount === 0
  ) {
    return null;
  }
  return (
    <div>
      <div className="flex items-center space-x-2">
        <h4 className="my-0">{t('UNAPPLIED_ADJUSTMENTS')}</h4>
        <Tooltip id="unapplied-adjustments-note">
          {t('UNAPPLIED_ADJUSTMENTS_TOOLTIP')}
        </Tooltip>
      </div>
      {receivablesCount > 0 && (
        <div className="mt-4">
          <div className="border-solid border-b border-grey-light">
            <h4 className="my-0 mb-2">{t('SHIPPER_CHARGES')}</h4>
          </div>
          <Adjustments
            onSelect={(item, checked) => onSelect('receivables', item, checked)}
            items={receivables}
            selectedItems={selectedReceivables}
            isItemsUnmapped={false}
          />
        </div>
      )}
      {payablesCount > 0 && (
        <div className="mt-4">
          <div className="border-solid border-b border-grey-light">
            <h4 className="my-0 mb-2">{t('CARRIER_CHARGES')}</h4>
          </div>
          <Adjustments
            onSelect={(item, checked) => onSelect('payables', item, checked)}
            items={payables}
            selectedItems={selectedPayables}
            isItemsUnmapped={false}
          />
        </div>
      )}
      {unmappedAccessorialRequestsCount > 0 && (
        <div className="mt-4">
          <div className="mb-4 border-solid border-b border-grey-light flex items-center space-x-2">
            <h4 className="my-0">{t('UNMAPPED_ACCESSORIALS')}</h4>
            <Tooltip id="unapplied-adjustments-note">
              {t('UNMAPPED_ACCESSORIALS_TOOLTIP')}
            </Tooltip>
          </div>
          <Adjustments
            onSelect={(item, checked) =>
              onSelect('accessorials', item, checked)
            }
            items={unmappedAccessorialRequests}
            selectedItems={selectedUnmappedAccessorialRequests}
            isItemsUnmapped={true}
          />
        </div>
      )}
    </div>
  );
}

OrphanAdjustments.propTypes = {
  receivables: PropTypes.array,
  payables: PropTypes.array,
  unmappedAccessorialRequests: PropTypes.array,
  selectedPayables: PropTypes.array,
  selectedReceivables: PropTypes.array,
  selectedUnmappedAccessorialRequests: PropTypes.array,
  onSelect: PropTypes.func.isRequired,
};

OrphanAdjustments.defaultProps = {
  receivables: [],
  payables: [],
  unmappedAccessorialRequests: [],
  selectedPayables: [],
  selectedReceivables: [],
  selectedUnmappedAccessorialRequests: [],
};
